/**
 * Хук+helpers для taxonomy (роли + отделы из backend).
 * Один react-query сessionScope cache — больше нигде не дублируется.
 *
 * Также: applies CSS-переменные --role-{key}-bg/fg и --dept-{key}-bg/fg на :root
 * по умолчанию + по custom-настройкам админа из localStorage.
 */
import { useQuery } from "@tanstack/react-query";
import { fetchProfilesMeta, type ProfilesMeta, type TaxonomyEntry } from "@/api/dashboard";

const COLOR_PALETTE: Record<string, { bg: string; fg: string }> = {
  green:   { bg: "#dcedc8", fg: "#33691e" },
  blue:    { bg: "#e3f2fd", fg: "#1565c0" },
  purple:  { bg: "#ede7f6", fg: "#4527a0" },
  pink:    { bg: "#fce4ec", fg: "#ad1457" },
  orange:  { bg: "#ffe0b2", fg: "#b35400" },
  red:     { bg: "#ffcdd2", fg: "#b71c1c" },
  teal:    { bg: "#b2dfdb", fg: "#00695c" },
  gray:    { bg: "#eceff1", fg: "#455a64" },
  brown:   { bg: "#d7ccc8", fg: "#4e342e" },
  indigo:  { bg: "#c5cae9", fg: "#283593" },
  lime:    { bg: "#f0f4c3", fg: "#827717" },
  ochre:   { bg: "#fff9c4", fg: "#9e7c00" },
  magenta: { bg: "#f8bbd0", fg: "#880e4f" },
  slate:   { bg: "#cfd8dc", fg: "#37474f" },
  mint:    { bg: "#c8e6c9", fg: "#1b5e20" },
  lilac:   { bg: "#e1bee7", fg: "#6a1b9a" },
  navy:    { bg: "#bbdefb", fg: "#0d47a1" },
  coral:   { bg: "#ffccbc", fg: "#bf360c" },
};

export const PALETTE_LIST = Object.entries(COLOR_PALETTE).map(([key, v]) => ({ key, ...v }));

export function useTaxonomy() {
  return useQuery<ProfilesMeta>({
    queryKey: ["profiles/meta/taxonomy"],
    queryFn: fetchProfilesMeta,
    staleTime: 5 * 60_000,  // 5 мин
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/** Получить style для pill из taxonomy-key + custom-color-map (из admin Settings).
 *  Если custom не задан — берём default из taxonomy. */
export function pillStyle(
  key: string | null | undefined,
  defaults: TaxonomyEntry[],
  customColors: Record<string, string>,
): { background: string; color: string } | null {
  if (!key) return null;
  const def = defaults.find(e => e.key === key);
  if (!def) return null;
  const colorKey = customColors[key] || def.default_color;
  const palette = COLOR_PALETTE[colorKey] || COLOR_PALETTE.gray;
  return { background: palette.bg, color: palette.fg };
}

export function pillStyleSimple(
  key: string | null | undefined,
  defaults: TaxonomyEntry[],
  customColors: Record<string, string>,
): { background: string; color: string } | null {
  if (!key) return null;
  const def = defaults.find(e => e.key === key);
  if (!def) return null;
  const colorKey = customColors[key] || def.default_color;
  const palette = COLOR_PALETTE[colorKey] || COLOR_PALETTE.gray;
  return { background: palette.bg, color: palette.fg };
}

export function loadCustomColors(storageKey: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch { return {}; }
}
